import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, PauseCircle } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { fmtRelative } from '@/features/dossier/components/primitives'
import { getDashboard, type Dashboard } from './api'
import { Cost, Reach, StatusChip } from './components'

/**
 * The overview.
 *
 * ## "Went out" and "arrived" are two numbers, and they are both here
 *
 * The whole module is arranged around that distinction, so the board that summarises it has to
 * show both. A single "sent" figure would be the number of messages this platform managed to
 * hand to a provider — which is the number an operator cares least about.
 *
 * ## The sending window is on the board, not buried in settings
 *
 * A campaign approved at 20:55 will pause at 21:00 and resume in the morning. Somebody watching
 * a send stall needs the reason on the same screen as the stall.
 */
export function CommsOverviewPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<Dashboard | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await getDashboard())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => {
    void load()
    // A send in progress moves; 30s is fast enough to watch it and slow enough not to flicker.
    const timer = setInterval(() => void load(), 30_000)
    return () => clearInterval(timer)
  }, [load])

  if (error && !data) return <ErrorStrip message={error} />
  if (!data) return <LoadingBlock label="Reading the room" />

  const undelivered = data.sentToday - data.deliveredToday

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Communications"
        description="What the campaign is saying, to whom, and whether it arrived."
      />

      {error && <ErrorStrip message={error} />}

      {!data.sendingEnabled && (
        <p className="inline-flex items-center gap-1.5 rounded border border-rose-500/30
                      bg-rose-500/5 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-400">
          <PauseCircle className="size-3.5" />
          Sending is switched off. Scheduled campaigns are holding and will resume when it is
          switched back on.
        </p>
      )}

      <KpiStrip>
        <MetricCard label="Drafts" value={data.campaignsDraft} sub="Not approved by anybody" />
        <MetricCard label="Scheduled" value={data.campaignsScheduled}
                    sub="Approved, waiting for their time" />
        <MetricCard label="Sending now" value={data.campaignsSending} sub="In flight" />
        <MetricCard label="Went out today" value={data.sentToday}
                    sub="Accepted by a provider" />
        <MetricCard label="Arrived today" value={data.deliveredToday}
                    sub="Confirmed by a delivery report" />
      </KpiStrip>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
        <Panel>
          <div className="p-2">
            <p className="text-[11px] font-medium">Recent campaigns</p>
            <ul className="mt-2 space-y-1.5">
              {data.recent.length === 0 && (
                <li className="py-8 text-center text-[12px] text-muted-foreground">
                  Nothing has been sent yet. A campaign starts as a draft and reaches nobody
                  until a person approves it.
                </li>
              )}
              {data.recent.map((c) => (
                <li key={c.id}>
                  <button type="button" onClick={() => navigate(`/comms/campaigns/${c.id}`)}
                          className="w-full rounded border border-border p-2 text-left
                                     hover:bg-muted/40">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="text-[12px] font-medium">{c.displayName}</span>
                      <StatusChip code={c.statusCode} />
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span className="text-[10px] text-muted-foreground">{c.ref}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {c.channelName ?? 'no channel'}
                      </span>
                      <Reach recipients={c.recipientCount} suppressed={c.suppressedCount} />
                      <Cost value={c.actualCostKes ?? c.estimatedCostKes}
                            prefix={c.actualCostKes ? '' : 'est. '} />
                      <span className="text-[10px] text-muted-foreground">
                        {fmtRelative(c.createdAt)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Panel>

        <div className="space-y-3">
          <Panel>
            <div className="space-y-1.5 p-3">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-medium">
                <Clock className="size-3.5" /> Sending window
              </p>
              <p className="text-[12px]">
                {String(data.quietHoursStart).padStart(2, '0')}:00 to{' '}
                {String(data.quietHoursEnd).padStart(2, '0')}:00 EAT
              </p>
              <p className="text-[10px] text-muted-foreground">
                Enforced when a message is sent, not when it is scheduled. A campaign outside
                the window is held until it opens — never dropped.
              </p>
            </div>
          </Panel>

          <Panel>
            <div className="space-y-1 p-3">
              <p className="text-[11px] font-medium">Today</p>
              <dl className="space-y-0.5 text-[11px]">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Accepted by a provider</dt>
                  <dd className="tabular-nums">{data.sentToday.toLocaleString()}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Confirmed delivered</dt>
                  <dd className="tabular-nums">{data.deliveredToday.toLocaleString()}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Awaiting a report</dt>
                  <dd className="tabular-nums">
                    {undelivered > 0 ? undelivered.toLocaleString() : 0}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Failed or undelivered</dt>
                  <dd className="tabular-nums text-rose-600">
                    {data.failedToday.toLocaleString()}
                  </dd>
                </div>
                <div className="flex justify-between border-t border-border pt-0.5">
                  <dt className="text-muted-foreground">Spent</dt>
                  <dd><Cost value={data.spentTodayKes} /></dd>
                </div>
              </dl>
              <p className="text-[10px] text-muted-foreground">
                Accepted means a provider took it. Only a delivery report says it arrived, and
                some providers never send one.
              </p>
            </div>
          </Panel>

          <Panel>
            <div className="space-y-1 p-3">
              <p className="text-[11px] font-medium">Listening</p>
              <button type="button" onClick={() => navigate('/comms/replies')}
                      className="block w-full rounded border border-border p-2 text-left
                                 text-[11px] hover:bg-muted/40">
                <b className="tabular-nums">{data.repliesUnhandled}</b> replies nobody has read
              </button>
              <button type="button" onClick={() => navigate('/comms/suppressions')}
                      className="block w-full rounded border border-border p-2 text-left
                                 text-[11px] hover:bg-muted/40">
                <b className="tabular-nums">{data.suppressionsLive}</b> on the do-not-contact
                list
              </button>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}
